import React from 'react';

// Utils
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';
import { SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import {
  HOME_BASE_TYPE_LABELS,
  PICKUP_OPTION_LABELS,
  APPROVED_DESTINATION_LABELS,
} from '../../util/fulfillmentHelpers.js';

import CustomExtendedDataSection, {
  SectionText,
} from '../../components/CustomExtendedDataSection/index.js';
import css from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.module.css';
import PropertyGroup from '../../components/PropertyGroup/PropertyGroup.js';

import { truncateAddressToCity } from '../../util/truncateAddressToCity';

import AccordionSection from './AccordionSection';
import SectionAmenities from './SectionAmenities';
import SectionVesselSpecs from './SectionVesselSpecs';
import SectionAllowedActivities from './SectionAllowedActivities';

// Fields that have custom rendering (excluded from CustomExtendedDataSection paths)
const DELIVERY_FIELDS = [
  'home_base_type',
  'pickup_options',
  'approved_destinations',
  'delivery_zone_1_label',
  'delivery_zone_1_fee',
  'delivery_zone_2_label',
  'delivery_zone_2_fee',
  'delivery_zone_3_label',
  'delivery_zone_3_fee',
  'delivery_notes',
  'third_party_notes',
];

const BOAT_DETAIL_FIELDS = ['boat_make', 'boat_model', 'boat_type'];
const BOAT_DETAIL_HEADINGS = new Set(['Make', 'Model', 'Boat Type']); // label-based fallback

// Fields that must never appear anywhere on the public listing page
const GLOBALLY_HIDDEN_FIELDS = [
  'booking_window',
  'availability_end_date',
  'fuel_policy',
  'minimum_renter_age',
  'minimum_participant_age',
  'damage_deposit',
  'charter_damage_deposit',
  'cancellation_policy',
  'deposit_hold_days',
  'approved_captains',
  'cancellation_policy_details',
];

// Label-based fallback for hosted config keys that don't match the strings above
const GLOBALLY_HIDDEN_LABELS = new Set([
  'Booking Window',
  'Availability End Date',
  'Fuel Policy',
  'Minimum Renter Age',
  'Minimum Participant Age',
  'Trip Authorization',
  'Damage Deposit',
  'Cancellation Policy',
  'Approved Captains',
  'Cancellation Policy Details',
]);

const HIDDEN_LISTING_FIELDS = [...GLOBALLY_HIDDEN_FIELDS, ...DELIVERY_FIELDS, ...BOAT_DETAIL_FIELDS];
const CURRENCY_LISTING_FIELDS = [];
const BOAT_LISTING_TYPES = ['boat-rental', 'captained-charter-private', 'captained-charter-ticketed'];

// Multi-enum fields merged into amenities grid (boat features only — no safety equipment)
const AMENITY_FIELD_KEYS = ['boat_features', 'included_accessories'];
const AMENITY_FIELD_HEADINGS = new Set(['Boat Features', 'Included Accessories']);

// Allowed Activities — standalone visible section (not accordion, not amenity grid)
const ALLOWED_ACTIVITIES_KEYS = ['allowed_activities'];
const ALLOWED_ACTIVITIES_HEADINGS = new Set(['Allowed Activities']);

// Multi-enum fields rendered inside accordions (safety equipment moves here)
const ACCORDION_FIELD_KEYS = ['safety_equipment', 'experience_required', 'approved_waters'];
const ACCORDION_FIELD_HEADINGS = new Set([
  'Safety Equipment',
  'Experience Required',
  'Experience Requirements',
  'Renter Requirements',
  'Approved Waters',
]);

const FIELDS_RENDERED_SEPARATELY = [
  ...AMENITY_FIELD_KEYS,
  ...ALLOWED_ACTIVITIES_KEYS,
  ...ACCORDION_FIELD_KEYS,
];
const HEADINGS_RENDERED_SEPARATELY = new Set([
  ...AMENITY_FIELD_HEADINGS,
  ...ALLOWED_ACTIVITIES_HEADINGS,
  ...ACCORDION_FIELD_HEADINGS,
]);

// Human-readable headings for accordion fields
const ACCORDION_HEADINGS = {
  safety_equipment: 'Safety Equipment',
  experience_required: 'Renter Requirements',
  approved_waters: 'Approved Waters',
};

const CANCELLATION_POLICY_DESCRIPTIONS = {
  'flexible': 'Flexible — full refund up to 24 hours before; no refund less than 24 hours',
  'flexible-48': 'Flexible — full refund up to 48 hours before; no refund less than 48 hours',
  'moderate': 'Moderate — full refund up to 7 days before; 50% refund less than 7 days; no refund less than 24 hours',
  'strict': 'Strict — full refund up to 30 days before; 50% refund less than 30 days; no refund less than 7 days',
  'no-refund': 'No Refund — all sales final',
};

// ── Trip Authorization ────────────────────────────────────────────────────────

const TripAuthorizationSection = ({ publicData, intl }) => {
  const damageDeposit = publicData?.damage_deposit;
  const charterDamageDeposit = publicData?.charter_damage_deposit;

  const tripAuthAmount =
    damageDeposit > 0 ? damageDeposit :
    charterDamageDeposit > 0 ? charterDamageDeposit :
    0;

  if (!(tripAuthAmount > 0)) return null;

  const formatDollars = amount =>
    intl.formatNumber(amount, { style: 'currency', currency: 'USD' });

  return (
    <div>
      <ul className={css.details}>
        <li className={css.detailsRow}>
          <span className={css.detailLabel}>Trip Authorization</span>
          <span>{formatDollars(tripAuthAmount)}</span>
        </li>
        <li className={css.detailsRow}>
          <span className={css.detailLabel}>Initial Claim Window</span>
          <span>48 hours after rental end</span>
        </li>
      </ul>
      <p style={{ margin: '4px 0 12px', fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
        Your card is saved at booking but not charged. After the rental ends, the provider has 48
        hours to file an initial damage claim or fuel receipt. If a claim is filed, you'll be
        notified and have 3 days to respond. A final repair invoice may be submitted up to 90 days
        after the rental if the repair process requires it. No funds are charged to your card unless
        a claim is filed and approved.
      </p>
    </div>
  );
};

// ── Cancellation Policy ───────────────────────────────────────────────────────

const CancellationDepositSection = ({ publicData, intl }) => {
  const cancellationPolicy = publicData?.cancellation_policy;
  const cancellationPolicyDetails = publicData?.cancellation_policy_details;

  const hasContent = cancellationPolicy || cancellationPolicyDetails;
  if (!hasContent) return null;

  const fullDescription = cancellationPolicy
    ? (CANCELLATION_POLICY_DESCRIPTIONS[cancellationPolicy] || cancellationPolicy)
    : null;
  const [cancellationLabel, cancellationDetail] = fullDescription
    ? fullDescription.split(' — ')
    : [null, null];

  return (
    <div>
      <ul className={css.details}>
        {cancellationLabel ? (
          <li className={css.detailsRow}>
            <span className={css.detailLabel}>Cancellation Policy</span>
            <span>{cancellationLabel}</span>
          </li>
        ) : null}
      </ul>
      {cancellationDetail ? (
        <p style={{ margin: '4px 0 12px', fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
          {cancellationDetail.charAt(0).toUpperCase() + cancellationDetail.slice(1)}
        </p>
      ) : null}
      {cancellationPolicyDetails ? (
        <SectionText heading="Cancellation Details" text={cancellationPolicyDetails} />
      ) : null}
    </div>
  );
};

// ── Availability & Delivery ──────────────────────────────────────────────────

const AvailabilityDeliverySection = ({ publicData, intl }) => {
  const homeBaseType = publicData?.home_base_type;
  const locationAddress = publicData?.location?.address;
  const cityState = locationAddress ? truncateAddressToCity(locationAddress) : null;
  const pickupOptions = publicData?.pickup_options || [];
  const approvedDestinations = publicData?.approved_destinations || [];
  const deliveryNotes = publicData?.delivery_notes;
  const thirdPartyNotes = publicData?.third_party_notes;

  if (!homeBaseType && pickupOptions.length === 0) return null;

  const showProviderDelivers = pickupOptions.includes('provider-delivers');
  const showThirdParty = pickupOptions.includes('third-party-ok');

  const homeBaseTypeLabel = homeBaseType ? HOME_BASE_TYPE_LABELS[homeBaseType] || homeBaseType : null;
  const homeBaseLabel = homeBaseTypeLabel || null;
  const pickupLabels = pickupOptions.map(o => PICKUP_OPTION_LABELS[o] || o);
  const destinationLabels = approvedDestinations.map(d => APPROVED_DESTINATION_LABELS[d] || d);

  const zones = [
    { label: publicData?.delivery_zone_1_label, fee: publicData?.delivery_zone_1_fee },
    { label: publicData?.delivery_zone_2_label, fee: publicData?.delivery_zone_2_fee },
    { label: publicData?.delivery_zone_3_label, fee: publicData?.delivery_zone_3_fee },
  ].filter(z => z.label && z.fee != null);

  const formatFee = cents =>
    intl.formatNumber(cents / 100, { style: 'currency', currency: 'USD' });

  return (
    <div>
      {homeBaseLabel ? (
        <div className={css.bulletSection}>
          <p className={css.bulletHeading}>Home Base</p>
          <ul className={css.bulletList}>
            <li className={css.bulletItem}>{homeBaseLabel}</li>
            {cityState ? <li className={css.bulletItem}>{cityState}</li> : null}
          </ul>
        </div>
      ) : null}

      {pickupLabels.length > 0 ? (
        <div className={css.bulletSection}>
          <p className={css.bulletHeading}>Pickup Options</p>
          <ul className={css.bulletList}>
            {pickupLabels.map(label => (
              <li key={label} className={css.bulletItem}>{label}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {showProviderDelivers ? (
        <>
          {destinationLabels.length > 0 ? (
            <div className={css.bulletSection}>
              <p className={css.bulletHeading}>Approved Delivery Destinations</p>
              <ul className={css.bulletList}>
                {destinationLabels.map(label => (
                  <li key={label} className={css.bulletItem}>{label}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {zones.length > 0 ? (
            <section className={css.sectionText}>
              <h2 className={css.sectionHeading}>Delivery Zones</h2>
              <ul className={css.details}>
                {zones.map(z => (
                  <li key={z.label} className={css.detailsRow}>
                    <span className={css.detailLabel}>{z.label}</span>
                    <span>{formatFee(z.fee)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {deliveryNotes ? <SectionText heading="Delivery Notes" text={deliveryNotes} /> : null}
        </>
      ) : null}

      {showThirdParty && thirdPartyNotes ? (
        <div className={css.bulletSection}>
          <p className={css.bulletHeading}>3rd Party Delivery Notes</p>
          <ul className={css.bulletList}>
            <li className={css.bulletItem}>{thirdPartyNotes}</li>
          </ul>
        </div>
      ) : null}

      {showProviderDelivers ? (
        <p className={css.disclaimer}>
          Delivery locations are determined solely by the provider. BoatersNetwork does not guarantee availability at any specific location. Final confirmation is subject to provider approval.
        </p>
      ) : null}
    </div>
  );
};

// ── Renter Requirements age fields ───────────────────────────────────────────

const RenterRequirementsAgeFields = ({ publicData }) => {
  const minimumRenterAge = publicData?.minimum_renter_age;
  const minimumParticipantAge = publicData?.minimum_participant_age;

  if (minimumRenterAge == null && minimumParticipantAge == null) return null;

  return (
    <ul className={css.details}>
      {minimumRenterAge != null ? (
        <li className={css.detailsRow}>
          <span className={css.detailLabel}>Minimum Renter Age</span>
          <span>{minimumRenterAge}</span>
        </li>
      ) : null}
      {minimumParticipantAge != null ? (
        <li className={css.detailsRow}>
          <span className={css.detailLabel}>Minimum Participant Age</span>
          <span>{minimumParticipantAge}</span>
        </li>
      ) : null}
    </ul>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };

  const allPropsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  // Boat make/model/type and delivery fields are rendered elsewhere — exclude from SectionText path
  const propsForCustomFields = allPropsForCustomFields.filter(
    p =>
      !BOAT_DETAIL_FIELDS.includes(p.key) &&
      !BOAT_DETAIL_HEADINGS.has(p.heading) &&
      !DELIVERY_FIELDS.includes(p.key)
  );

  const listingType = publicData?.listingType;
  const isBoatListingType = BOAT_LISTING_TYPES.includes(listingType);
  let boatFieldsSeeded = false;

  const pickExtendedDataFields = (filteredConfigs, config) => {
    // Pre-seed boat make/model at the very top of the details list on first reduce call
    if (!boatFieldsSeeded) {
      boatFieldsSeeded = true;
      if (isBoatListingType) {
        const boatItems = [
          publicData?.boat_make ? { key: 'boat_make', value: publicData.boat_make, label: 'Make' } : null,
          publicData?.boat_model ? { key: 'boat_model', value: publicData.boat_model, label: 'Model' } : null,
        ].filter(Boolean);
        filteredConfigs = [...boatItems, ...filteredConfigs];
      }
    }

    const { key, schemaType, enumOptions, showConfig = {} } = config;

    if (HIDDEN_LISTING_FIELDS.includes(key)) {
      return filteredConfigs;
    }

    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = typeof publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      if (CURRENCY_LISTING_FIELDS.includes(key) && typeof value === 'number') {
        const formattedValue = intl.formatNumber(value, { style: 'currency', currency: 'USD' });
        return filteredConfigs.concat({ key, value: formattedValue, label });
      }

      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      if (detailValue) {
        const UNIT_SUFFIXES = { boat_length: 'ft', boat_capacity_weight: 'lbs', boat_horsepower: 'hp' };
        const suffix = UNIT_SUFFIXES[key];
        const finalValue = suffix ? { ...detailValue, value: `${detailValue.value} ${suffix}` } : detailValue;
        return filteredConfigs.concat(finalValue);
      }
      return filteredConfigs;
    }
    return filteredConfigs;
  };

  // Compute vessel spec fields to pass to SectionVesselSpecs
  const allDetailFields = listingFieldConfigs.reduce(pickExtendedDataFields, []);

  // Prepend boat_type as the very first spec row
  // Use allPropsForCustomFields (already has formatted options) with key+heading fallback
  const boatTypeField = allPropsForCustomFields.find(
    p => p.key === 'boat_type' || p.heading === 'Boat Type'
  );
  let vesselSpecFields = allDetailFields;
  if (boatTypeField) {
    const selected = boatTypeField.selectedOptions || [];
    const labels = (boatTypeField.options || [])
      .filter(o => selected.includes(o.key))
      .map(o => o.label);
    if (labels.length > 0) {
      vesselSpecFields = [
        { key: 'boat_type', label: 'Boat Type', value: labels.join(', ') },
        ...allDetailFields,
      ];
    }
  }

  // Move Horsepower to just above Propulsion Type
  const hpIdx = vesselSpecFields.findIndex(
    f => f.key === 'boat_horsepower' || f.label === 'Horsepower'
  );
  const propulsionIdx = vesselSpecFields.findIndex(
    f => f.key === 'propulsion_type' || f.label === 'Propulsion Type'
  );
  if (hpIdx !== -1 && propulsionIdx !== -1 && hpIdx !== propulsionIdx - 1) {
    const hpField = vesselSpecFields[hpIdx];
    const withoutHp = [
      ...vesselSpecFields.slice(0, hpIdx),
      ...vesselSpecFields.slice(hpIdx + 1),
    ];
    const insertAt = withoutHp.findIndex(
      f => f.key === 'propulsion_type' || f.label === 'Propulsion Type'
    );
    vesselSpecFields = [
      ...withoutHp.slice(0, insertAt),
      hpField,
      ...withoutHp.slice(insertAt),
    ];
  }

  // Separate amenity, allowed-activities, and accordion fields
  // Use key + heading to survive hosted config key mismatches
  const amenityFields = allPropsForCustomFields.filter(
    p => AMENITY_FIELD_KEYS.includes(p.key) || AMENITY_FIELD_HEADINGS.has(p.heading)
  );
  const allowedActivitiesField = allPropsForCustomFields.find(
    p => ALLOWED_ACTIVITIES_KEYS.includes(p.key) || ALLOWED_ACTIVITIES_HEADINGS.has(p.heading)
  );
  const accordionFields = allPropsForCustomFields.filter(
    p => ACCORDION_FIELD_KEYS.includes(p.key) || ACCORDION_FIELD_HEADINGS.has(p.heading)
  );
  const regularCustomFields = propsForCustomFields.filter(
    p =>
      p.schemaType !== SCHEMA_TYPE_MULTI_ENUM &&
      !FIELDS_RENDERED_SEPARATELY.includes(p.key) &&
      !HEADINGS_RENDERED_SEPARATELY.has(p.heading) &&
      !GLOBALLY_HIDDEN_LABELS.has(p.heading)
  );

  const hasDeliverySection =
    (publicData?.home_base_type || (publicData?.pickup_options || []).length > 0);

  return (
    <>
      {/* Vessel Features — boat_features + included_accessories merged grid */}
      <SectionAmenities amenityFields={amenityFields} />

      {/* Allowed Activities — standalone visible section with collapse (not accordion) */}
      {allowedActivitiesField && (allowedActivitiesField.options || []).length > 0 ? (
        <SectionAllowedActivities
          heading="Activities"
          options={allowedActivitiesField.options}
          selectedOptions={allowedActivitiesField.selectedOptions}
        />
      ) : null}

      {/* Vessel specs — collapsible, shows 4 rows by default */}
      <SectionVesselSpecs fields={vesselSpecFields} fuelPolicy={publicData?.fuel_policy} />

      {/* Regular custom fields (not amenities, not accordion-handled) */}
      {/* Pass empty fieldConfigs so SectionDetails doesn't double-render specs */}
      <CustomExtendedDataSection
        sectionDetailsProps={{ ...props, isFieldForCategory: isFieldForSelectedCategories, fieldConfigs: [], heading: null }}
        propsForCustomFields={regularCustomFields}
        idPrefix="listingPage"
        pickExtendedDataFields={pickExtendedDataFields}
      />

      {/* Renter Requirements accordion */}
      {(() => {
        const f = accordionFields.find(
          af => af.key === 'experience_required' || af.heading === 'Experience Required' || af.heading === 'Experience Requirements' || af.heading === 'Renter Requirements'
        );
        const hasSelected = f && (f.selectedOptions || []).length > 0;
        const hasAgeFields =
          publicData?.minimum_renter_age != null || publicData?.minimum_participant_age != null;
        if (!hasSelected && !hasAgeFields) return null;
        return (
          <AccordionSection heading="Renter Requirements">
            <RenterRequirementsAgeFields publicData={publicData} />
            {hasSelected ? (
              <PropertyGroup
                id="listingPage.experience_required"
                ariaLabel="Renter Requirements"
                options={(f.options || []).filter(o => (f.selectedOptions || []).includes(o.key))}
                selectedOptions={f.selectedOptions || []}
                twoColumns={(f.options || []).length > 5}
                showUnselectedOptions={false}
              />
            ) : null}
          </AccordionSection>
        );
      })()}

      {/* Accordion sections: safety_equipment, approved_waters (experience_required rendered above) */}
      {accordionFields
        .filter(
          f =>
            f.key !== 'experience_required' &&
            f.heading !== 'Experience Required' &&
            f.heading !== 'Experience Requirements' &&
            f.heading !== 'Renter Requirements'
        )
        .map(f => {
          const heading = ACCORDION_HEADINGS[f.key] || f.heading || f.key;
          const hasSelected = (f.selectedOptions || []).length > 0;
          if (!hasSelected) return null;
          return (
            <AccordionSection key={f.key} heading={heading}>
              <PropertyGroup
                id={`listingPage.${f.key}`}
                ariaLabel={heading}
                options={(f.options || []).filter(o => (f.selectedOptions || []).includes(o.key))}
                selectedOptions={f.selectedOptions || []}
                twoColumns={(f.options || []).length > 5}
                showUnselectedOptions={false}
              />
            </AccordionSection>
          );
        })}

      {/* Trip Authorization accordion */}
      {(() => {
        const damageDeposit = publicData?.damage_deposit;
        const charterDamageDeposit = publicData?.charter_damage_deposit;
        const tripAuthAmount =
          damageDeposit > 0 ? damageDeposit :
          charterDamageDeposit > 0 ? charterDamageDeposit :
          0;
        if (!(tripAuthAmount > 0)) return null;
        return (
          <AccordionSection heading="Trip Authorization">
            <TripAuthorizationSection publicData={publicData} intl={intl} />
          </AccordionSection>
        );
      })()}

      {/* Cancellation Policy accordion */}
      {(publicData?.cancellation_policy || publicData?.cancellation_policy_details) ? (
        <AccordionSection heading="Cancellation Policy">
          <CancellationDepositSection publicData={publicData} intl={intl} />
        </AccordionSection>
      ) : null}

      {/* Availability & Delivery accordion */}
      {hasDeliverySection ? (
        <AccordionSection heading="Availability & Delivery">
          <AvailabilityDeliverySection publicData={publicData} intl={intl} />
        </AccordionSection>
      ) : null}
    </>
  );
};

export default CustomListingFields;
